import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Star } from 'lucide-react';
import { adminApi, formatDate } from '@kardume/services';
import type { MerchantActiveFeatureDto, MerchantFeatureStatusDto } from '@kardume/types';

/**
 * "Sua loja está em destaque" - no cabeçalho de todas as telas do painel
 * (card 262).
 *
 * Hoje o lojista não tem como saber. O destaque é criado pelo comercial no
 * backoffice, a loja sobe na vitrine, e a primeira vez que ele percebe que
 * existia é quando acaba e o movimento cai: um benefício que só se manifesta
 * como perda, e que ele não consegue nem renovar porque não sabia que tinha.
 *
 * Fica no layout, e não na Dashboard, de propósito. Quem entra no painel para
 * responder uma avaliação ou validar um cupom não passa pela Dashboard, e é
 * justamente enquanto o destaque está no ar que a informação vale - depois é só
 * histórico.
 *
 * Silencioso quando não há destaque, e silencioso quando a chamada falha: um
 * cabeçalho que pisca erro em toda navegação, para uma informação acessória,
 * ensina a ignorar o cabeçalho.
 */
const FeaturedBanner: React.FC = () => {
  const [status, setStatus] = useState<MerchantFeatureStatusDto | null>(null);

  useEffect(() => {
    let active = true;
    adminApi
      .getMyFeatures()
      .then((result) => {
        if (active) setStatus(result);
      })
      .catch(() => {
        if (active) setStatus(null);
      });
    return () => {
      active = false;
    };
  }, []);

  if (!status?.featured || status.features.length === 0) return null;

  return (
    <Banner>
      <Star size={18} />
      <div className="body">
        <strong>Sua loja está em destaque no Kardume Urbano.</strong>
        <ul>
          {status.features.map((feature, index) => (
            /* Destaque não tem id nesta resposta de propósito - o painel não
               opera destaque, só o vê -, então a chave é a posição. A lista é
               curta e não reordena entre renders. */
            // eslint-disable-next-line react/no-array-index-key
            <li key={index}>{describe(feature)}</li>
          ))}
        </ul>
      </div>
    </Banner>
  );
};

export default FeaturedBanner;

/**
 * Uma linha em português, com contexto e período.
 *
 * O contexto não é enfeite: "em destaque" sem recorte não quer dizer nada numa
 * plataforma que atende várias cidades, e o lojista de Toledo que lesse
 * "destaque" achando que é na plataforma inteira ficaria com uma expectativa
 * que ninguém vendeu para ele.
 */
function describe(feature: MerchantActiveFeatureDto): string {
  const where =
    feature.scope === 'LOCALITY'
      ? `em ${feature.contextName ?? 'uma cidade'}`
      : feature.scope === 'CATEGORY'
        ? `na categoria ${feature.contextName ?? '-'}`
        : 'em toda a plataforma';
  const until = feature.endsAt
    ? `até ${formatDate(feature.endsAt)}`
    : 'enquanto a parceria estiver ativa';
  return `Destaque ${where}, desde ${formatDate(feature.startsAt)}, ${until}.`;
}

/* -------------------------------------------------------------------------- */

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  margin-bottom: 1.25rem;
  padding: 0.85rem 1rem;
  border-radius: ${(p) => p.theme.radiusLg};
  border: 1px solid ${(p) => p.theme.primary}33;
  background: ${(p) => p.theme.primary}12;

  > svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${(p) => p.theme.primary};
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }

  ul {
    margin: 0;
    padding-left: 1.1rem;
    font-size: 0.82rem;
    line-height: 1.5;
    color: ${(p) => p.theme.textSecondary};
  }
`;
